import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosInstance } from '../../config/axios';
import { extractErrorMessage } from '../../utils/errorHandling';
import {
	ErrorMessage,
	LoadingGif,
	LoadingMessage,
} from '../Shared/CommonStyles';

import * as S from './FlappyImageManager.styles';

interface FlappyImageItem {
	_id: string;
	original_name: string;
	upload_date: string;
	url?: string;
}

interface PendingFile {
	id: string;
	file: File;
	previewUrl: string;
}

interface PendingUploadState {
	total: number;
	progressPct: number;
	isUploading: boolean;
}

const MAX_FILES_PER_BATCH = 200; // server limit (multer .array('images', 200))

const FlappyImageManager: React.FC = () => {
	const queryClient = useQueryClient();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [uploadError, setUploadError] = useState<string | null>(null);
	const [deleteError, setDeleteError] = useState<string | null>(null);
	const [deletingId, setDeletingId] = useState<string | null>(null);
	const [pending, setPending] = useState<PendingFile[]>([]);
	const [isDragOver, setIsDragOver] = useState(false);
	const [dims, setDims] = useState<Record<string, string>>({});
	const [uploadState, setUploadState] = useState<PendingUploadState>({
		total: 0,
		progressPct: 0,
		isUploading: false,
	});

	// Revoke pending preview object URLs when leaving the page.
	useEffect(() => {
		return () => {
			pending.forEach((p) => URL.revokeObjectURL(p.previewUrl));
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const {
		data: images,
		isLoading,
		error,
	} = useQuery<FlappyImageItem[]>({
		queryKey: ['flappy-images'],
		queryFn: async () => {
			const res = await axiosInstance.get('/api/admin/flappy-images?signed=1');
			return res.data;
		},
		staleTime: 1000 * 60 * 60, // 1 hour — images rarely change
		refetchOnWindowFocus: false,
	});

	const addFiles = (files: File[]) => {
		const imgs = files.filter((f) => f.type.startsWith('image/'));
		const rejected = files.length - imgs.length;
		if (imgs.length === 0) {
			setUploadError(
				rejected > 0
					? 'Només es poden pujar imatges (PNG, JPG, WebP…).'
					: null
			);
			return;
		}
		setUploadError(rejected > 0 ? `${rejected} arxiu${rejected > 1 ? 's' : ''} ignorat${rejected > 1 ? 's' : ''}: no ${rejected > 1 ? 'són' : 'és'} imatge${rejected > 1 ? 's' : ''}.` : null);
		const overLimit = Math.min(imgs.length + pending.length, MAX_FILES_PER_BATCH);
		const accepted = imgs.slice(0, Math.max(0, overLimit - pending.length));
		if (accepted.length < imgs.length) {
			setUploadError(
				`Màxim ${MAX_FILES_PER_BATCH} imatges per lot.`
			);
		}
		setPending((prev) => [
			...prev,
			...accepted.map((f) => ({
				id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
				file: f,
				previewUrl: URL.createObjectURL(f),
			})),
		]);
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(e.target.files || []);
		if (files.length > 0) {
			addFiles(files);
			setUploadState({ total: 0, progressPct: 0, isUploading: false });
		}
		if (fileInputRef.current) fileInputRef.current.value = '';
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragOver(false);
		if (!uploadState.isUploading) addFiles(Array.from(e.dataTransfer.files || []));
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragOver(true);
	};

	const handleDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragOver(false);
	};

	const removePending = (id: string) => {
		setPending((prev) => {
			const p = prev.find((x) => x.id === id);
			if (p) URL.revokeObjectURL(p.previewUrl);
			return prev.filter((x) => x.id !== id);
		});
	};

	const resetPending = () => {
		setPending((prev) => {
			prev.forEach((p) => URL.revokeObjectURL(p.previewUrl));
			return [];
		});
	};

	const handleUpload = async () => {
		if (pending.length === 0 || uploadState.isUploading) return;
		setUploadError(null);
		setUploadState({ total: pending.length, progressPct: 0, isUploading: true });

		const files = pending.map((p) => p.file);
		const formData = new FormData();
		files.forEach((file) => {
			formData.append('images', file);
		});

		try {
			// Use axiosInstance so token refresh interceptor works
			await axiosInstance.post('/api/admin/flappy-images/upload', formData, {
				headers: { 'Content-Type': 'multipart/form-data' },
				onUploadProgress: (progressEvent) => {
					if (progressEvent.total) {
						const pct = Math.round((progressEvent.loaded / progressEvent.total) * 100);
						setUploadState((prev) => ({ ...prev, progressPct: Math.min(pct, 99) }));
					}
				},
			});
			resetPending();
			queryClient.invalidateQueries({ queryKey: ['flappy-images'] });
		} catch (err: any) {
			setUploadError(extractErrorMessage(err));
		} finally {
			setUploadState({ total: files.length, progressPct: 100, isUploading: false });
			setTimeout(() => setUploadState({ total: 0, progressPct: 0, isUploading: false }), 750);
		}
	};

	const handleDelete = async (id: string) => {
		if (!window.confirm('Segur que vols eliminar aquesta imatge?')) return;
		setDeleteError(null);
		setDeletingId(id);
		try {
			await axiosInstance.delete(`/api/admin/flappy-images/${id}`);
			queryClient.invalidateQueries({ queryKey: ['flappy-images'] });
		} catch (err: any) {
			setDeleteError(extractErrorMessage(err));
		} finally {
			setDeletingId(null);
		}
	};

	const recordDims = (id: string) => (e: React.SyntheticEvent<HTMLImageElement>) => {
		const img = e.currentTarget;
		if (img.naturalWidth > 0) {
			setDims((prev) => ({ ...prev, [id]: `${img.naturalWidth}×${img.naturalHeight}` }));
		}
	};

	if (isLoading) {
		return (
			<S.ManagerContainer>
				<LoadingMessage>
					<LoadingGif size="large" />
				</LoadingMessage>
			</S.ManagerContainer>
		);
	}

	if (error) {
		return (
			<S.ManagerContainer>
				<ErrorMessage>Error: {extractErrorMessage(error)}</ErrorMessage>
			</S.ManagerContainer>
		);
	}

	const count = images?.length ?? 0;

	return (
		<S.ManagerContainer>
			<S.SectionTitle>Imatges del Flappy Senglar</S.SectionTitle>
			<S.Hint>
				Un sticker aleatori per a cada canó del joc (jugadors amb rol Cap,
				Agrupament o Admin). S'empastren a tota l'amplada del canó, així que
				funcionen millor imatges verticals.{' '}
				{count > 0
					? `Ara n'hi ha ${count}.`
					: 'Encara no n\u2019hi ha cap: sense stickers es juguen amb canons verds.'}
			</S.Hint>

			<S.DropZone
				$dragOver={isDragOver}
				onDrop={handleDrop}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onClick={() =>
					!uploadState.isUploading && fileInputRef.current?.click()
				}
			>
				<input
					ref={fileInputRef}
					type="file"
					accept="image/*"
					multiple
					onChange={handleFileChange}
					disabled={uploadState.isUploading}
					onClick={(e) => e.stopPropagation()}
				/>
				<S.DropZoneIcon>🖼️</S.DropZoneIcon>
				<S.DropZoneMainText>
					Arrossega les imatges aquí, o clica per triar-les
				</S.DropZoneMainText>
				<S.DropZoneSubText>
					PNG · JPG · WebP (transparència recomanada) · màx. {MAX_FILES_PER_BATCH} per lot
				</S.DropZoneSubText>
			</S.DropZone>

			{pending.length > 0 && !uploadState.isUploading && (
				<S.PendingRow>
					{pending.map((p) => (
						<S.PendingChip key={p.id}>
							<img src={p.previewUrl} alt={p.file.name} />
							<span title={p.file.name}>{p.file.name}</span>
							<button
								type='button'
								title='Treure de la llista'
								onClick={() => removePending(p.id)}
							>
								×
							</button>
						</S.PendingChip>
					))}
				</S.PendingRow>
			)}

			<S.ActionsRow>
				{uploadState.isUploading ? (
					<>
						<S.ProgressLabel>Pujant… {uploadState.progressPct}%</S.ProgressLabel>
						<S.ProgressBarContainer>
							<S.ProgressBarFill $progress={uploadState.progressPct} />
						</S.ProgressBarContainer>
					</>
				) : (
					<>
						<S.UploadButton
							type='button'
							onClick={handleUpload}
							disabled={pending.length === 0}
						>
							Pujar {pending.length > 0 ? `${pending.length} imatge${pending.length > 1 ? 's' : ''}` : ''}
						</S.UploadButton>
						{pending.length > 0 && (
							<S.CancelButton type='button' onClick={resetPending}>
								Buidar llista
							</S.CancelButton>
						)}
					</>
				)}
				{uploadError && <ErrorMessage>{uploadError}</ErrorMessage>}
			</S.ActionsRow>

			{deleteError && (
				<ErrorMessage>
					No s'ha pogut eliminar: {deleteError}
				</ErrorMessage>
			)}

			{count === 0 ? (
				<S.EmptyMessage>No hi ha imatges pujades.</S.EmptyMessage>
			) : (
				<S.ImageGrid>
					{images!.map((img) => (
						<S.ImageCard key={img._id} $deleting={deletingId === img._id}>
							<S.ThumbBox>
								{img.url ? (
									<S.ImageThumb
										src={img.url}
										alt={img.original_name}
										loading='lazy'
										decoding='async'
										onLoad={recordDims(img._id)}
									/>
								) : (
									<S.ImageThumb
										src='data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22120%22 height=%2280%22%3E%3Crect fill=%22%23ddd%22 width=%22120%22 height=%2280%22/%3E%3C/svg%3E'
										alt='No disponible'
									/>
								)}
							</S.ThumbBox>
							<S.ImageName title={img.original_name}>
								{img.original_name}
							</S.ImageName>
							<S.ImageMeta>
								{dims[img._id] && <span>{dims[img._id]}</span>}
								<span>
									{new Date(img.upload_date).toLocaleDateString('ca-ES')}
								</span>
							</S.ImageMeta>
							<S.DeleteButton
								type='button'
								onClick={() => handleDelete(img._id)}
								disabled={uploadState.isUploading || deletingId !== null}
							>
								{deletingId === img._id ? 'Eliminant…' : 'Eliminar'}
							</S.DeleteButton>
						</S.ImageCard>
					))}
				</S.ImageGrid>
			)}
		</S.ManagerContainer>
	);
};

export default FlappyImageManager;